/**
 * ============================================
 * COUNTRY → REGION (country filter grouping)
 * ============================================
 *
 * One fixed ISO-3166 alpha-2 → region map, shared by the Analytics,
 * Multiplayer and Sony/DOMO country filters so the grouping is identical on
 * every tab. The regions are exactly Lokko's PlayStation Store listing
 * (DevNet > Concept 10013643 > PlayStationStore, 2026-10); any country not in
 * that listing goes to "Non-SIE", as do players with no country ('UNKNOWN').
 */

export const UNKNOWN_COUNTRY = 'UNKNOWN';

/** PlayStation's own regions, used to group countries. */
export const REGION_ORDER = ['SIEE', 'SIEA', 'SIEAsia', 'SIEJ', 'Non-SIE'] as const;
export type Region = (typeof REGION_ORDER)[number];
export const REGION_INFO: Record<Region, string> = {
  SIEA: 'Americas',
  SIEE: 'Europe, Middle East, Africa, Oceania, India',
  SIEAsia: 'SIEJA-Asia',
  SIEJ: 'SIEJA-Japan',
  'Non-SIE': 'Not in the PlayStation Store listing, or no country',
};

// Lokko's PlayStation Store countries per region, as listed on DevNet.
// (Netherlands is listed under SIEE but not selected for the store.)
const SIE_COUNTRIES: Record<Exclude<Region, 'Non-SIE'>, string[]> = {
  SIEA: ['AR', 'BO', 'BR', 'CA', 'CL', 'CO', 'CR', 'EC', 'SV', 'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'US', 'UY'],
  SIEE: [
    'AU', 'AT', 'BH', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS',
    'IN', 'IE', 'IL', 'IT', 'KW', 'LB', 'LU', 'MT', 'NL', 'NZ', 'NO', 'OM', 'PL', 'PT', 'QA',
    'RO', 'RU', 'SA', 'SK', 'SI', 'ZA', 'ES', 'SE', 'CH', 'TR', 'UA', 'AE', 'GB',
  ],
  SIEAsia: ['CN', 'HK', 'ID', 'MY', 'SG', 'KR', 'TW', 'TH'],
  SIEJ: ['JP'],
};

const REGION_OF: Record<string, Region> = {};
for (const [region, codes] of Object.entries(SIE_COUNTRIES)) {
  for (const code of codes) REGION_OF[code] = region as Region;
}

export function regionOf(code: string): Region {
  return REGION_OF[(code || '').toUpperCase()] ?? 'Non-SIE';
}

let displayNames: Intl.DisplayNames | null = null;
try {
  displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
  displayNames = null;
}

/** English country name for an ISO-2 code (falls back to the code itself). */
export function countryName(code: string): string {
  if (!code || code === UNKNOWN_COUNTRY) return 'Unknown';
  try {
    return displayNames?.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

export interface CountryOption {
  code: string;
  name: string;
  /** shown next to the name, e.g. players */
  count: number;
}

/** Selection semantics shared by every tab: an empty list means "All". */
export function makeCountryPredicate(selected: string[]): (code: string | null | undefined) => boolean {
  if (!selected.length) return () => true;
  const set = new Set(selected.map(c => c.toUpperCase()));
  return code => set.has((code || UNKNOWN_COUNTRY).toUpperCase() || UNKNOWN_COUNTRY);
}

export interface CountryGroup {
  region: Region;
  options: CountryOption[];
  count: number;
}

/** Options grouped by SIE region (biggest country first). A query matching a
 *  country name or code returns just those countries; otherwise a query
 *  matching a region name or description returns the whole region. */
export function groupCountryOptions(options: CountryOption[], query = ''): CountryGroup[] {
  const q = query.trim().toLowerCase();
  const countryHit = (o: CountryOption) => o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q;
  const anyCountryHit = !!q && options.some(countryHit);
  const byRegion = new Map<Region, CountryOption[]>();
  for (const o of options) {
    const r = regionOf(o.code);
    const regionHit = !anyCountryHit && !!q && (r.toLowerCase().includes(q) || REGION_INFO[r].toLowerCase().includes(q));
    if (q && !regionHit && !countryHit(o)) continue;
    const list = byRegion.get(r);
    if (list) list.push(o); else byRegion.set(r, [o]);
  }
  return REGION_ORDER
    .filter(r => byRegion.has(r))
    .map(r => {
      const list = byRegion.get(r)!.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      return { region: r, options: list, count: list.reduce((n, o) => n + o.count, 0) };
    });
}
